import { insideRangeCheck } from '@/utils/date';
import { DateTime } from 'luxon';
import { Allocation } from '../Timeline.types';

export type AllocationCount = { inRange: number; total: number };

export type OwnAndForeignAllocationCount = { own: AllocationCount; foreign: { inRange: number } };

export function countAllocationsInRange(allocations: Allocation[], start: DateTime, end: DateTime): number {
    return allocations.filter((allocation) =>
        insideRangeCheck(allocation.startDateTime, allocation.endDateTime, start, end),
    ).length;
}

export function countAllocationsByLocation(
    allocationsByLocationId: Map<string, Allocation[]>,
    start: DateTime,
    end: DateTime,
): Map<string, AllocationCount> {
    return new Map(
        Array.from(allocationsByLocationId, ([locationId, allocations]) => [
            locationId,
            {
                inRange: countAllocationsInRange(allocations, start, end),
                total: allocations.length,
            },
        ]),
    );
}

export function countOwnAndForeignAllocationsByLocation(
    allocationsByLocationId: Map<string, Allocation[]>,
    foreignAllocationIds: Set<string>,
    start: DateTime,
    end: DateTime,
): Map<string, OwnAndForeignAllocationCount> {
    return new Map(
        Array.from(allocationsByLocationId, ([locationId, allocations]) => {
            const ownAllocations = allocations.filter((allocation) => !foreignAllocationIds.has(allocation.id));
            const foreignAllocations = allocations.filter((allocation) => foreignAllocationIds.has(allocation.id));
            return [
                locationId,
                {
                    own: {
                        inRange: countAllocationsInRange(ownAllocations, start, end),
                        total: ownAllocations.length,
                    },
                    foreign: { inRange: countAllocationsInRange(foreignAllocations, start, end) },
                },
            ];
        }),
    );
}

export function requireAllocationCount<T>(allocationCountByLocationId: Map<string, T>, locationId: string): T {
    const allocationCount = allocationCountByLocationId.get(locationId);
    if (!allocationCount) {
        throw new Error(`No allocation count for location ${locationId}`);
    }
    return allocationCount;
}

export function sumAllocationCounts(allocationCounts: AllocationCount[]): AllocationCount {
    return allocationCounts.reduce(
        (sum, allocationCount) => ({
            inRange: sum.inRange + allocationCount.inRange,
            total: sum.total + allocationCount.total,
        }),
        {
            inRange: 0,
            total: 0,
        },
    );
}

export function sumOwnAndForeignAllocationCounts(
    allocationCounts: OwnAndForeignAllocationCount[],
): OwnAndForeignAllocationCount {
    return {
        own: sumAllocationCounts(allocationCounts.map((allocationCount) => allocationCount.own)),
        foreign: {
            inRange: allocationCounts.reduce((sum, allocationCount) => sum + allocationCount.foreign.inRange, 0),
        },
    };
}
